import React from 'react';

// Skeleton components
export function CardSkeleton({ className = '' }) {
  return (
    <div className={`ps-card-static p-5 ${className}`}>
      <div className="h-4 w-3/4 bg-muted rounded animate-skeleton mb-3" />
      <div className="h-3 w-1/2 bg-muted rounded animate-skeleton mb-2" />
      <div className="h-3 w-2/3 bg-muted rounded animate-skeleton" />
    </div>
  );
}

export function GridSkeleton({ count = 6 }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {Array.from({ length: count }).map((_, i) => (
        <CardSkeleton key={i} />
      ))}
    </div>
  );
}

export function ListSkeleton({ count = 5 }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="ps-card-static p-4 flex items-center gap-4">
          <div className="h-10 w-10 rounded-full bg-muted animate-skeleton" />
          <div className="flex-1">
            <div className="h-4 w-1/3 bg-muted rounded animate-skeleton mb-2" />
            <div className="h-3 w-1/2 bg-muted rounded animate-skeleton" />
          </div>
        </div>
      ))}
    </div>
  );
}

// Status Badge
export function StatusBadge({ status }) {
  let cls = 'badge-draft';
  if (status === 'Active' || status === 'Closed' || status === 'Complete') cls = 'badge-active';
  else if (status === 'Improving' || status === 'Onboarding') cls = 'badge-improving';
  else if (status === 'Critical') cls = 'badge-critical';
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60" />
      {status || 'Unknown'}
    </span>
  );
}

// Procedure Type Pill
export function TypePill({ type }) {
  if (!type) return null;
  let cls = 'pill-core-execution';
  if (type.includes('Support')) cls = 'pill-support-admin';
  else if (type.includes('Compliance')) cls = 'pill-compliance';
  else if (type.includes('Maintenance')) cls = 'pill-maintenance';
  else if (type.includes('Control')) cls = 'pill-control';
  
  return (
    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${cls}`}>{type}</span>
  );
}

// Trigger Pill
export function TriggerPill({ trigger }) {
  if (!trigger) return null;
  let cls = 'pill-scheduled';
  if (trigger.includes('Incoming')) cls = 'pill-incoming-request';
  else if (trigger.includes('Event')) cls = 'pill-event-based';
  else if (trigger.includes('Compliance')) cls = 'pill-compliance-required';
  
  return (
    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${cls}`}>{trigger}</span>
  );
}

// Empty State
export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="empty-state">
      {Icon && <Icon size={48} className="text-muted-foreground mb-4 opacity-40" />}
      <h3 className="text-lg font-semibold mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground max-w-md mb-4">{description}</p>
      {action && (
        <button
          onClick={action.onClick}
          className="px-4 py-2 rounded-xl text-sm font-medium"
          style={{ background: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))' }}
          data-testid="empty-state-primary-action"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}

// KPI Status Helper
export function getKpiStatus(current, target) {
  if (current === null || current === undefined) return { label: 'No Data', color: 'grey', cls: 'kpi-grey' };
  if (target === null || target === undefined) return { label: 'No Target', color: 'grey', cls: 'kpi-grey' };
  if (target === 0) {
    if (current === 0) return { label: 'On Target', color: 'green', cls: 'kpi-green' };
    return { label: 'Above Target', color: 'red', cls: 'kpi-red' };
  }
  var ratio = current / target;
  if (ratio >= 1) return { label: 'On Target', color: 'green', cls: 'kpi-green' };
  if (ratio >= 0.9) return { label: 'Near Target', color: 'orange', cls: 'kpi-orange' };
  return { label: 'Below Target', color: 'red', cls: 'kpi-red' };
}

// Page Header
export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-3">{children}</div>}
    </div>
  );
}

// ============================================================
// MODERN NOTION BLOCK RENDERER
// ============================================================

export function NotionBlockRenderer({ blocks, onProcedureClick }) {
  if (!blocks || blocks.length === 0) return null;
  
  // Group consecutive list items together
  var groupedBlocks = [];
  var currentList = null;
  
  blocks.forEach(function(block, idx) {
    if (block.type === 'bulleted_list_item' || block.type === 'numbered_list_item') {
      if (currentList && currentList.type === block.type) {
        currentList.items.push(block);
      } else {
        if (currentList) groupedBlocks.push(currentList);
        currentList = { type: block.type, items: [block], id: 'list-' + idx };
      }
    } else {
      if (currentList) {
        groupedBlocks.push(currentList);
        currentList = null;
      }
      groupedBlocks.push(block);
    }
  });
  if (currentList) groupedBlocks.push(currentList);
  
  return React.createElement('div', { className: 'notion-content-modern' },
    groupedBlocks.map(function(item, idx) {
      if (item.items) {
        // Render grouped list
        var tag = item.type === 'numbered_list_item' ? 'ol' : 'ul';
        var listCls = item.type === 'numbered_list_item' ? 'notion-ol' : 'notion-ul';
        return React.createElement(tag, { key: item.id, className: listCls },
          item.items.map(function(li, liIdx) {
            var liText = renderRichTextModern(li.content);
            var liChildren = null;
            if (li.children && li.children.length > 0) {
              liChildren = React.createElement(NotionBlockRenderer, { blocks: li.children, onProcedureClick: onProcedureClick });
            }
            return React.createElement('li', { key: li.id || liIdx, className: 'notion-li' }, liText, liChildren);
          })
        );
      }
      return renderModernBlock(item, idx, onProcedureClick);
    })
  );
}

function renderRichTextModern(content) {
  if (!content || content.length === 0) return null;
  return content.map(function(part, i) {
    var text = part.text || '';
    if (!text) return null;
    
    var props = { key: i };
    
    if (part.href) {
      props.href = part.href;
      props.className = 'notion-link';
      props.target = '_blank';
      props.rel = 'noopener noreferrer';
      return React.createElement('a', props, text);
    }
    if (part.bold && part.italic) {
      props.className = 'notion-bold-italic';
      return React.createElement('strong', props, React.createElement('em', null, text));
    }
    if (part.bold) {
      props.className = 'notion-bold';
      return React.createElement('strong', props, text);
    }
    if (part.italic) {
      props.className = 'notion-italic';
      return React.createElement('em', props, text);
    }
    if (part.code) {
      props.className = 'notion-inline-code';
      return React.createElement('code', props, text);
    }
    if (part.underline) {
      props.className = 'notion-underline';
      return React.createElement('span', props, text);
    }
    if (part.strikethrough) {
      props.className = 'notion-strikethrough';
      return React.createElement('s', props, text);
    }
    return React.createElement('span', props, text);
  });
}

function detectBlockStyle(block) {
  var plainText = block.plain_text || '';
  var upper = plainText.toUpperCase().trim();
  
  // Detect scripts (quoted dialogue)
  if (plainText.indexOf('"') !== -1 && plainText.length > 40) {
    var quoteCount = (plainText.match(/"/g) || []).length;
    if (quoteCount >= 2) return 'script';
  }
  
  // Detect checklist items
  if (plainText.indexOf('\u2611') !== -1 || plainText.indexOf('\u2610') !== -1) return 'checklist';
  
  // Detect important rules/warnings
  if (upper.startsWith('RULE') || upper.startsWith('CRITICAL') || upper.startsWith('MANDATORY') || 
      upper.startsWith('IMPORTANT') || upper.startsWith('WARNING') || upper.startsWith('ERROR')) return 'warning';
  
  // Detect escalation
  if (upper.startsWith('ESCALAT')) return 'escalation';
  
  return 'normal';
}

function renderModernBlock(block, idx, onProcedureClick) {
  var key = block.id || idx;
  var text = renderRichTextModern(block.content);
  var plainText = block.plain_text || '';
  var childElements = null;
  
  if (block.children && block.children.length > 0) {
    childElements = React.createElement('div', { className: 'notion-children' },
      block.children.map(function(child, ci) {
        return renderModernBlock(child, ci, onProcedureClick);
      })
    );
  }

  var style = detectBlockStyle(block);

  switch (block.type) {
    case 'paragraph':
      if (!plainText.trim()) return React.createElement('div', { key: key, className: 'notion-spacer' });
      
      if (style === 'script') {
        return React.createElement('div', { key: key, className: 'notion-script' },
          React.createElement('div', { className: 'notion-script-icon' }, '\ud83d\udcac'),
          React.createElement('div', { className: 'notion-script-text' }, text, childElements)
        );
      }
      if (style === 'checklist') {
        return React.createElement('div', { key: key, className: 'notion-check-item' }, text, childElements);
      }
      if (style === 'warning') {
        return React.createElement('div', { key: key, className: 'notion-warning' },
          React.createElement('div', { className: 'notion-warning-icon' }, '\u26a0\ufe0f'),
          React.createElement('div', null, text, childElements)
        );
      }
      if (style === 'escalation') {
        return React.createElement('div', { key: key, className: 'notion-escalation' },
          React.createElement('div', { className: 'notion-escalation-icon' }, '\ud83d\udea8'),
          React.createElement('div', null, text, childElements)
        );
      }
      return React.createElement('p', { key: key, className: 'notion-p' }, text, childElements);
    
    case 'heading_1':
      return React.createElement('h1', { key: key, className: 'notion-h1' }, text);
    case 'heading_2':
      return React.createElement('h2', { key: key, className: 'notion-h2' }, text);
    case 'heading_3':
      return React.createElement('h3', { key: key, className: 'notion-h3' }, text);
    
    case 'to_do':
      var checkedCls = block.checked ? 'notion-todo-done' : 'notion-todo-open';
      return React.createElement('div', { key: key, className: 'notion-todo ' + checkedCls },
        React.createElement('span', { className: 'notion-todo-check' }, block.checked ? '\u2705' : '\u2b1c'),
        React.createElement('span', { className: 'notion-todo-text' }, text)
      );
    
    case 'callout':
      return React.createElement('div', { key: key, className: 'notion-callout' },
        block.icon ? React.createElement('span', { className: 'notion-callout-icon' }, block.icon) : null,
        React.createElement('div', { className: 'notion-callout-content' }, text, childElements)
      );
    
    case 'code':
      return React.createElement('div', { key: key, className: 'notion-code-block' },
        block.language ? React.createElement('div', { className: 'notion-code-lang' }, block.language) : null,
        React.createElement('pre', null, React.createElement('code', null, plainText))
      );
    
    case 'divider':
      return React.createElement('div', { key: key, className: 'notion-divider' });
    
    case 'quote':
      return React.createElement('blockquote', { key: key, className: 'notion-quote' }, text, childElements);
    
    case 'image':
      return block.url ? React.createElement('div', { key: key, className: 'notion-image-wrap' },
        React.createElement('img', { src: block.url, alt: block.caption || '', className: 'notion-image' }),
        block.caption ? React.createElement('p', { className: 'notion-image-caption' }, block.caption) : null
      ) : null;
    
    case 'toggle':
      return React.createElement('details', { key: key, className: 'notion-toggle' },
        React.createElement('summary', { className: 'notion-toggle-summary' }, text),
        childElements
      );
    
    case 'bookmark':
      return React.createElement('a', { key: key, href: block.url, className: 'notion-bookmark', target: '_blank', rel: 'noopener noreferrer' },
        React.createElement('span', null, block.caption || block.url || 'Link')
      );
    
    default:
      return text ? React.createElement('p', { key: key, className: 'notion-p' }, text) : null;
  }
}
